import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined } from "@ant-design/icons";
import { Button, Progress, Space, Typography } from "antd";
import { useMemo, useState } from "react";
import type { QuizQuestion } from "../lib/types";

const { Title, Text } = Typography;

type TypeformQuizProps = {
  questions: QuizQuestion[];
  answers: Record<string, string>;
  submitting?: boolean;
  onAnswer: (questionId: string, value: string) => void;
  onSubmit: () => void;
  onExit?: () => void;
};

export function TypeformQuiz({
  questions,
  answers,
  submitting = false,
  onAnswer,
  onSubmit,
  onExit
}: TypeformQuizProps) {
  const initialStep = useMemo(() => {
    const firstUnanswered = questions.findIndex((q) => !answers[q.id]);
    return firstUnanswered >= 0 ? firstUnanswered : 0;
  }, []);
  const [stepIndex, setStepIndex] = useState(initialStep);
  const question = questions[stepIndex];
  const selected = question ? answers[question.id] : undefined;
  const isLast = stepIndex >= questions.length - 1;
  const allAnswered = questions.every((q) => answers[q.id]);
  const progressPercent = questions.length
    ? Math.round(((stepIndex + (selected ? 1 : 0)) / questions.length) * 100)
    : 0;

  const choiceLabels = useMemo(() => ["A", "B", "C", "D"], []);

  if (!question) {
    return null;
  }

  const goNext = () => {
    if (!selected) return;
    if (isLast) {
      if (allAnswered) void onSubmit();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, questions.length - 1));
  };

  const goBack = () => {
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  return (
    <div className="typeform-quiz-shell">
      <div style={{ marginBottom: 24 }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text type="secondary">
            Question {stepIndex + 1} of {questions.length}
          </Text>
          {onExit ? (
            <Button type="link" size="small" onClick={onExit} disabled={submitting}>
              Exit quiz
            </Button>
          ) : null}
        </Space>
        <Progress
          percent={progressPercent}
          showInfo={false}
          strokeColor="#1677ff"
          style={{ marginTop: 8 }}
        />
      </div>

      <div key={question.id} className="typeform-quiz-step">
        <span className="typeform-tag">{question.conceptTag}</span>
        <Title level={3} style={{ marginTop: 16, marginBottom: 32, fontWeight: 600 }}>
          {question.prompt}
        </Title>

        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {question.choices.map((choice, idx) => {
            const active = selected === choice;
            return (
              <button
                key={choice}
                type="button"
                className={`typeform-choice${active ? " typeform-choice--active" : ""}`}
                onClick={() => onAnswer(question.id, choice)}
                disabled={submitting}
              >
                <span className="typeform-choice__label">{choiceLabels[idx] ?? idx + 1}</span>
                <span className="typeform-choice__text">{choice}</span>
              </button>
            );
          })}
        </Space>
      </div>

      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={goBack}
          disabled={stepIndex === 0 || submitting}
        >
          Back
        </Button>
        {isLast ? (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => void onSubmit()}
            loading={submitting}
            disabled={!allAnswered}
          >
            Submit quiz
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            onClick={goNext}
            disabled={!selected || submitting}
          >
            Continue
          </Button>
        )}
      </div>

      <style>{`
        .typeform-quiz-shell {
          max-width: 640px;
          margin: 0 auto;
          padding: 8px 4px 24px;
        }
        .typeform-quiz-step {
          animation: typeformFadeIn 0.35s ease;
        }
        @keyframes typeformFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .typeform-tag {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 999px;
          background: #f0f5ff;
          color: #1677ff;
          font-size: 12px;
          font-weight: 500;
        }
        .typeform-choice {
          display: flex;
          align-items: center;
          gap: 16px;
          width: 100%;
          text-align: left;
          padding: 16px 20px;
          border: 2px solid #e8e8e8;
          border-radius: 12px;
          background: #fff;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
          font: inherit;
          color: inherit;
        }
        .typeform-choice:hover:not(:disabled) {
          border-color: #91caff;
          box-shadow: 0 2px 8px rgba(22, 119, 255, 0.08);
        }
        .typeform-choice--active {
          border-color: #1677ff;
          background: #f0f5ff;
          box-shadow: 0 2px 12px rgba(22, 119, 255, 0.12);
        }
        .typeform-choice:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .typeform-choice__label {
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          border-radius: 8px;
          background: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 13px;
        }
        .typeform-choice--active .typeform-choice__label {
          background: #1677ff;
          color: #fff;
        }
        .typeform-choice__text {
          flex: 1;
          line-height: 1.45;
        }
      `}</style>
    </div>
  );
}
